/**
 * Practice programs on the device (pure — unit tested): which practice the quick menu ("Æfing
 * dagsins") offers, links into a practice's songs, and the program editor's list operations.
 */
import type { EventDTO } from '../../shared/dto/events';
import { practiceListId, type ProgramItem } from '../../shared/program';
import { setlistSearch } from '../viewer/setlist';

/** A practice stays on offer this long after it ends (singers look the songs up afterwards). */
export const PRACTICE_AFTER_MS = 3 * 3_600_000;
/** How far ahead the next practice with songs is offered. */
export const PRACTICE_AHEAD_MS = 14 * 86_400_000;

/** The event has songs to practise (events cached before programs existed have none). */
export const hasProgram = (ev: Pick<EventDTO, 'program'>): boolean => Array.isArray(ev.program) && ev.program.length > 0;

/**
 * The practice the quick menu offers: the earliest event with songs that is not cancelled, has not
 * ended more than PRACTICE_AFTER_MS ago and starts within PRACTICE_AHEAD_MS — so the ongoing
 * practice, else today's, else the next one; null when there is none.
 */
export function pickPractice(events: readonly EventDTO[], now: number): EventDTO | null {
  let best: EventDTO | null = null;
  for (const ev of events) {
    if (ev.status !== 'scheduled' || !hasProgram(ev)) continue;
    if (ev.endsAt + PRACTICE_AFTER_MS <= now || ev.startsAt > now + PRACTICE_AHEAD_MS) continue;
    if (!best || ev.startsAt < best.startsAt || (ev.startsAt === best.startsAt && ev.id < best.id)) best = ev;
  }
  return best;
}

/** The song page of entry `index` of an event's practice (the song opens in the practice's list). */
export function practiceSongHref(eventId: string, index: number, entry: { songId: string; entryId: string }): string {
  return `/songs/${encodeURIComponent(entry.songId)}${setlistSearch(new URLSearchParams(), practiceListId(eventId), index, entry.entryId)}`;
}

/* ─── Program editor ────────────────────────────────────────────────────── */

/** Adds a song at the end (once: a song already on the program as itself is not added again). */
export function addSong(items: readonly ProgramItem[], songId: string, id: string): ProgramItem[] {
  if (items.some((it) => it.type === 'song' && it.songId === songId)) return [...items];
  return [...items, { id, type: 'song', songId }];
}

/** Adds a whole folder / setlist at the end (at most once). */
export function addCollection(items: readonly ProgramItem[], collectionId: string, id: string): ProgramItem[] {
  if (items.some((it) => it.type === 'collection' && it.collectionId === collectionId)) return [...items];
  return [...items, { id, type: 'collection', collectionId }];
}

/** Moves item `index` one place up (-1) or down (+1); unchanged at the ends. */
export function moveItem(items: readonly ProgramItem[], index: number, dir: -1 | 1): ProgramItem[] {
  const to = index + dir;
  if (index < 0 || index >= items.length || to < 0 || to >= items.length) return [...items];
  const out = [...items];
  [out[index], out[to]] = [out[to]!, out[index]!];
  return out;
}

export function removeItem(items: readonly ProgramItem[], itemId: string): ProgramItem[] {
  return items.filter((it) => it.id !== itemId);
}

const itemKey = (it: ProgramItem) => `${it.id}|${it.type}|${it.type === 'song' ? it.songId : it.collectionId}`;

/** Same items in the same order (nothing to save). */
export function sameProgram(a: readonly ProgramItem[], b: readonly ProgramItem[]): boolean {
  return a.length === b.length && a.every((it, i) => itemKey(it) === itemKey(b[i]!));
}
